import { formActions } from '@macs-frontend/web-macs/shared';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Action, createReducer, Store } from 'macs-ngrx-actions';
import {
  Actions,
  createFormGroupState,
  formGroupReducer,
  FormGroupState,
  reset,
  setErrors,
  setValue,
  updateGroup,
  validate,
} from 'ngrx-forms';
import { minLength, pattern, required } from 'ngrx-forms/validation';
import * as authActions from '../actions/auth.actions';

export const loginFormId = 'LOGIN_FORM';
export const resetPasswordFormId = 'RESET_PASSWORD_FORM';

export interface LoginForm {
  username: string;
  password: string;
}

export interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
}

export interface AuthState {
  token: string;
  loginForm: FormGroupState<LoginForm>;
  loading: boolean;
  resetPasswordForm: FormGroupState<ResetPasswordForm>;
}

export const getFeatureState = createFeatureSelector<AuthState>('auth');

export const getLoginForm = createSelector(getFeatureState, state => state.loginForm);

export const getToken = createSelector(getFeatureState, state => state.token);

export const getLoading = createSelector(getFeatureState, state => state.loading);

export const getResetPasswordForm = createSelector(
  getFeatureState,
  state => state.resetPasswordForm,
);

export const validateAndUpdateLoginForm = updateGroup<LoginForm>({
  username: validate(
    required,
    pattern(
      // eslint-disable-next-line max-len
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    ),
  ),
  password: validate(required),
});

export const validateAndUpdateResetPasswordForm = (
  resetPasswordForm: FormGroupState<ResetPasswordForm>,
) => {
  const isConfirmPasswordValid =
    resetPasswordForm.value.password === resetPasswordForm.value.confirmPassword;
  const confirmPasswordErrors = isConfirmPasswordValid ? {} : { noEqual: true };
  return updateGroup<ResetPasswordForm>({
    password: validate(required, minLength(8)),
    confirmPassword: setErrors(confirmPasswordErrors),
  })(resetPasswordForm);
};

const emptyLoginForm = validateAndUpdateLoginForm(
  createFormGroupState<LoginForm>(loginFormId, {
    username: '',
    password: '',
  }),
);

const emptyResetPasswordForm = createFormGroupState<ResetPasswordForm>(
  resetPasswordFormId,
  {
    // @ts-expect-error
    password: null,
    // @ts-expect-error
    confirmPassword: null,
  },
);

@Store<AuthState>({
  token: '',
  loginForm: emptyLoginForm,
  loading: false,
  resetPasswordForm: emptyResetPasswordForm,
})
export class AuthStore {
  // @ts-expect-error
  @Action(...formActions)
  formsChanged(state: AuthState, action: Actions<any>) {
    const loginForm = validateAndUpdateLoginForm(
      formGroupReducer(state.loginForm, action),
    );
    const resetPasswordForm = validateAndUpdateResetPasswordForm(
      formGroupReducer(state.resetPasswordForm, action),
    );
    return {
      ...state,
      loginForm: loginForm !== state.loginForm ? loginForm : state.loginForm,
      resetPasswordForm:
        resetPasswordForm !== state.resetPasswordForm
          ? resetPasswordForm
          : state.resetPasswordForm,
    };
  }

  @Action(authActions.LoginSuccess)
  loginSuccess(state: AuthState, action: authActions.LoginSuccess) {
    return {
      ...state,
      token: action.payload,
      loading: false,
    };
  }

  @Action(authActions.LoginFailed)
  loginFailed(state: AuthState) {
    const newValue: LoginForm = {
      username: state.loginForm.value.username,
      password: '',
    };
    const loginForm = setValue(state.loginForm, newValue);
    return {
      ...state,
      loginForm,
      loading: false,
    };
  }

  @Action(authActions.Login)
  login(state: AuthState) {
    return {
      ...state,
      loading: true,
    };
  }

  @Action(authActions.SetToken)
  setToken(state: AuthState, { token }: authActions.SetToken) {
    return {
      ...state,
      token,
    };
  }

  @Action(authActions.SendEmailFailed)
  sendEmailFailed(state: AuthState) {
    return {
      ...state,
      loginForm: setValue(state.loginForm, emptyLoginForm.value),
    };
  }

  @Action(authActions.SendEmailSuccess)
  sendEmailSuccess(state: AuthState) {
    return {
      ...state,
      loginForm: setErrors(state.loginForm, {}),
    };
  }

  @Action(authActions.ResetLoginForm)
  resetLoginForm(state: AuthState): AuthState {
    return {
      ...state,
      loginForm: reset(setErrors(state.loginForm, {})),
    };
  }
}

export function authReducer(state: any, action: any) {
  return createReducer(AuthStore)(state, action);
}
